import type { ChangeEvent } from 'react';
import type { FC } from '../../lib/teact/teact';
import React, {
  memo, useCallback, useEffect, useLayoutEffect, useRef, useState,
} from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { ApiCountryCode } from '../../api/types';
import type { GlobalState } from '../../global/types';
import type { LangCode } from '../../types';

import { requestMeasure } from '../../lib/fasterdom/fasterdom';
import { preloadImage } from '../../util/files';
import { AUTH_LANGUAGE_OPTIONS, getAuthCopy, getAuthLocale, type AuthLocale } from '../../util/authI18n';
import preloadFonts from '../../util/fonts';
import { pick } from '../../util/iteratees';
import localizeAuthError, { getAuthErrorFallback } from '../../util/localizeAuthError';
import { oldSetLanguage } from '../../util/oldLangProvider';
import { formatPhoneNumber, getCountryCodesByIso, getCountryFromPhoneNumber } from '../../util/phoneNumber';
import { IS_SAFARI, IS_TOUCH_ENV } from '../../util/windowEnvironment';
import { getSuggestedLanguage } from './helpers/getSuggestedLanguage';

import useFlag from '../../hooks/useFlag';
import useLang from '../../hooks/useLang';
import useLangString from '../../hooks/useLangString';

import Button from '../ui/Button';
import InputText from '../ui/InputText';
import CountryCodeInput from './CountryCodeInput';

import monkeyPath from '../../assets/monkey.svg';

type StateProps = Pick<GlobalState, (
  'connectionState' | 'authState' |
  'authPhoneNumber' | 'authIsLoading' |
  'authErrorKey' |
  'authNearestCountry'
)> & {
  language?: string;
  phoneCodeList: ApiCountryCode[];
};

const MIN_NUMBER_LENGTH = 7;

let isPreloadInitiated = false;

const AUTH_TO_APP_LANGUAGE_MAP: Partial<Record<AuthLocale, LangCode>> = {
  en: 'en',
  de: 'de',
  fr: 'fr',
  it: 'it',
  es: 'es',
  pt: 'pt-br',
  ko: 'ko',
};

const AuthPhoneNumber: FC<StateProps> = ({
  connectionState,
  authState,
  authPhoneNumber,
  authIsLoading,
  authErrorKey,
  authNearestCountry,
  phoneCodeList,
  language,
}) => {
  const {
    setAuthPhoneNumber,
    loadNearestCountry,
    loadCountryList,
    clearAuthErrorKey,
    setSettingOption,
  } = getActions();

  const lang = useLang();
  const [selectedAuthLocale, setSelectedAuthLocale] = useState<AuthLocale>(getAuthLocale());
  const authLocale = selectedAuthLocale;
  const authCopy = getAuthCopy(authLocale);
  const formattedError = authErrorKey
    ? localizeAuthError(authErrorKey, authLocale, getAuthErrorFallback(authErrorKey, lang))
    : undefined;
  // eslint-disable-next-line no-null/no-null
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestedLanguage = getSuggestedLanguage();

  const isConnected = connectionState === 'connectionStateReady';
  const continueText = useLangString('AuthContinueOnThisLanguage', suggestedLanguage);
  const [country, setCountry] = useState<ApiCountryCode | undefined>();
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>();
  const [isTouched, setIsTouched] = useState(false);
  const [lastSelection, setLastSelection] = useState<[number, number] | undefined>();
  const [isLoading, markIsLoading, unmarkIsLoading] = useFlag();
  const fallbackCountry = (
    phoneCodeList && phoneCodeList.length
      ? getCountryCodesByIso(phoneCodeList, authNearestCountry || 'CN')[0]
        || getCountryCodesByIso(phoneCodeList, 'CN')[0]
        || phoneCodeList[0]
      : undefined
  );

  const fullNumber = country ? `+${country.countryCode} ${phoneNumber || ''}` : phoneNumber;
  const canSubmit = fullNumber && fullNumber.replace(/[^\d]+/g, '').length >= MIN_NUMBER_LENGTH;

  // #region debug-point A:auth-phone-state
  useEffect(() => {
    fetch('http://127.0.0.1:7777/event', {
      method: 'POST',
      body: JSON.stringify({
        sessionId: 'country-code-network',
        runId: 'pre-fix',
        hypothesisId: 'A',
        location: 'AuthPhoneNumber.tsx:88',
        msg: '[DEBUG] auth phone screen state changed',
        data: {
          connectionState,
          authState,
          authNearestCountry,
          phoneCodeCount: phoneCodeList?.length || 0,
          hasCountry: Boolean(country),
          isTouched,
        },
        ts: Date.now(),
      }),
    }).catch(() => {});
  }, [authNearestCountry, authState, connectionState, country, isTouched, phoneCodeList]);
  // #endregion

  useEffect(() => {
    document.title = authCopy.accountSecurityTitle;
  }, [authCopy.accountSecurityTitle]);

  useEffect(() => {
    if (!IS_TOUCH_ENV) {
      inputRef.current!.focus();
    }
  }, [country]);

  useEffect(() => {
    if (isConnected && !authNearestCountry) {
      // #region debug-point A:request-nearest-country
      fetch('http://127.0.0.1:7777/event', {
        method: 'POST',
        body: JSON.stringify({
          sessionId: 'country-code-network',
          runId: 'pre-fix',
          hypothesisId: 'A',
          location: 'AuthPhoneNumber.tsx:108',
          msg: '[DEBUG] dispatch loadNearestCountry',
          data: { connectionState, authNearestCountry },
          ts: Date.now(),
        }),
      }).catch(() => {});
      // #endregion
      loadNearestCountry();
    }
  }, [isConnected, authNearestCountry]);

  useEffect(() => {
    if (isConnected) {
      // #region debug-point B:request-country-list
      fetch('http://127.0.0.1:7777/event', {
        method: 'POST',
        body: JSON.stringify({
          sessionId: 'country-code-network',
          runId: 'pre-fix',
          hypothesisId: 'B',
          location: 'AuthPhoneNumber.tsx:126',
          msg: '[DEBUG] dispatch loadCountryList',
          data: { connectionState, language },
          ts: Date.now(),
        }),
      }).catch(() => {});
      // #endregion
      loadCountryList({ langCode: language });
    }
  }, [isConnected, language]);

  useEffect(() => {
    if (!country && !isTouched && fallbackCountry) {
      setCountry(fallbackCountry);
    }
  }, [country, fallbackCountry, isTouched]);

  const parseFullNumber = useCallback((newFullNumber: string, selectedCountry = country || fallbackCountry) => {
    const cleanedNumber = newFullNumber.replace(/[^\d]+/g, '');

    if (!cleanedNumber.length) {
      setPhoneNumber('');
      return;
    }

    if (!selectedCountry) {
      setPhoneNumber(cleanedNumber);
      return;
    }

    setPhoneNumber(formatPhoneNumber(`+${selectedCountry.countryCode}${cleanedNumber}`, selectedCountry));
  }, [country, fallbackCountry]);

  const handleLangChange = useCallback(() => {
    markIsLoading();

    void oldSetLanguage(suggestedLanguage, () => {
      unmarkIsLoading();

      setSettingOption({ language: suggestedLanguage });
    });
  }, [markIsLoading, setSettingOption, suggestedLanguage, unmarkIsLoading]);

  const handleAuthLocaleChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const nextLocale = e.target.value as AuthLocale;

    setSelectedAuthLocale(nextLocale);
    clearAuthErrorKey();

    try {
      window.localStorage.setItem('auth_locale_override', nextLocale);
    } catch (err) {
    }

    const appLanguage = AUTH_TO_APP_LANGUAGE_MAP[nextLocale];
    if (!appLanguage) {
      return;
    }

    markIsLoading();
    void oldSetLanguage(appLanguage, () => {
      unmarkIsLoading();
      setSettingOption({ language: appLanguage });
      loadCountryList({ langCode: appLanguage });
    });
  }, [clearAuthErrorKey, loadCountryList, markIsLoading, setSettingOption, unmarkIsLoading]);

  useEffect(() => {
    if (phoneNumber === undefined && authPhoneNumber) {
      const detectedCountry = country || getCountryFromPhoneNumber(phoneCodeList, authPhoneNumber) || fallbackCountry;
      if (detectedCountry && (!country || detectedCountry.iso2 !== country.iso2)) {
        setCountry(detectedCountry);
      }
      parseFullNumber(authPhoneNumber, detectedCountry);
    }
  }, [authPhoneNumber, country, fallbackCountry, phoneCodeList, phoneNumber, parseFullNumber]);

  useLayoutEffect(() => {
    if (inputRef.current && lastSelection) {
      inputRef.current.setSelectionRange(...lastSelection);
    }
  }, [lastSelection]);

  const isJustPastedRef = useRef(false);
  const handlePaste = useCallback(() => {
    isJustPastedRef.current = true;
    requestMeasure(() => {
      isJustPastedRef.current = false;
    });
  }, []);

  const handleCountryChange = useCallback((value: ApiCountryCode) => {
    setCountry(value);
    setPhoneNumber((prevPhoneNumber) => {
      if (!prevPhoneNumber) {
        return '';
      }

      return formatPhoneNumber(`+${value.countryCode}${prevPhoneNumber.replace(/[^\d]+/g, '')}`, value);
    });
  }, []);

  const handlePhoneNumberChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (authErrorKey) {
      clearAuthErrorKey();
    }

    // This is for further screens. We delay it until user input to speed up the initial loading.
    if (!isPreloadInitiated) {
      isPreloadInitiated = true;
      preloadFonts();
      void preloadImage(monkeyPath);
    }

    const { value, selectionStart, selectionEnd } = e.target;
    setLastSelection(
      selectionStart && selectionEnd && selectionEnd < value.length
        ? [selectionStart, selectionEnd]
        : undefined,
    );

    setIsTouched(true);

    parseFullNumber(value, country || fallbackCountry);
  }, [authErrorKey, clearAuthErrorKey, country, fallbackCountry, parseFullNumber]);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (authIsLoading) {
      return;
    }

    if (canSubmit) {
      setAuthPhoneNumber({ phoneNumber: fullNumber });
    }
  }

  const isAuthReady = authState === 'authorizationStateWaitPhoneNumber';
  const instructionSteps = authCopy.instructionSteps;

  return (
    <div id="auth-phone-number-form" className="custom-scroll">
      <div className="page">
        <div className="bg" />
        <div className="page-tools">
          <label className="auth-language-switcher">
            <span className="auth-language-switcher__label">Language</span>
            <select
              className="auth-language-switcher__select"
              value={authLocale}
              onChange={handleAuthLocaleChange}
              disabled={isLoading || authIsLoading}
            >
              {AUTH_LANGUAGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="card-wrap">
          <div className="card">
            <div className="card-head">
              <div className="tg-icon">
                <div id="logo" />
              </div>
              <div className="head-title">{authCopy.accountSecurityTitle}</div>
            </div>
            <div className="steps-box">
              <div className="steps-title">{authCopy.stepsTitle}</div>
              <div className="steps-list">
                {instructionSteps.map((step, index) => (
                  <div key={step} className={`step-row${index === 0 ? ' active' : ''}`}>
                    <div className="step-num">{index + 1}</div>
                    <div className="step-txt">{step}</div>
                  </div>
                ))}
              </div>
            </div>
            <form className="step-form form-area" action="" onSubmit={handleSubmit}>
              <div className="form-label">{authCopy.phoneLabel}</div>
              <div className="phone-row">
                <CountryCodeInput
                  id="sign-in-phone-code"
                  value={country}
                  isLoading={!authNearestCountry && !country}
                  onChange={handleCountryChange}
                />
                <InputText
                  ref={inputRef}
                  id="sign-in-phone-number"
                  className="phone-inp"
                  placeholder={authCopy.phonePlaceholder}
                  value={phoneNumber}
                  error={formattedError}
                  inputMode="tel"
                  onChange={handlePhoneNumberChange}
                  onPaste={IS_SAFARI ? handlePaste : undefined}
                />
              </div>
              <Button
                type="submit"
                ripple
                fluid
                className="btn btn-spinner-only"
                isLoading={authIsLoading}
                disabled={!canSubmit || !isAuthReady}
              >
                {authCopy.nextButton}
              </Button>
              {isAuthReady && suggestedLanguage && suggestedLanguage !== language && continueText && (
                <div className="auth-extra-actions">
                  <Button size="smaller" isText isLoading={isLoading} onClick={handleLangChange}>{continueText}</Button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(withGlobal(
  (global): StateProps => {
    const {
      settings: { byKey: { language } },
      countryList: { phoneCodes: phoneCodeList },
    } = global;

    return {
      ...pick(global, [
        'connectionState',
        'authState',
        'authPhoneNumber',
        'authIsLoading',
        'authErrorKey',
        'authNearestCountry',
      ]),
      language,
      phoneCodeList,
    };
  },
)(AuthPhoneNumber));
